/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Edge Detect Node
   Sobel gradient operator for contour extraction and line art effects.
   ═══════════════════════════════════════════════════════════════ */

import type { NodeDefinition } from '../types';
import { GLSL_HEADER, GLSL_COLOR_UTILS } from '../../rendering/effects/glslCommon';
import { createShaderProcessor } from '../../rendering/effects/shaderProcessor';

const EDGE_DETECT_SHADER = `${GLSL_HEADER}
${GLSL_COLOR_UTILS}

uniform sampler2D u_image;
uniform vec2 u_resolution;
uniform float u_intensity;
uniform bool u_invert;
uniform float u_mixSource; // 0.0 .. 1.0

void main() {
  vec2 step = 1.0 / u_resolution;

  // 3x3 sample grid luminance
  float tl = getLuminance(texture(u_image, v_uv + vec2(-step.x, -step.y)).rgb);
  float tc = getLuminance(texture(u_image, v_uv + vec2(0.0, -step.y)).rgb);
  float tr = getLuminance(texture(u_image, v_uv + vec2(step.x, -step.y)).rgb);

  float ml = getLuminance(texture(u_image, v_uv + vec2(-step.x, 0.0)).rgb);
  float mr = getLuminance(texture(u_image, v_uv + vec2(step.x, 0.0)).rgb);

  float bl = getLuminance(texture(u_image, v_uv + vec2(-step.x, step.y)).rgb);
  float bc = getLuminance(texture(u_image, v_uv + vec2(0.0, step.y)).rgb);
  float br = getLuminance(texture(u_image, v_uv + vec2(step.x, step.y)).rgb);

  // Sobel convolution
  float gx = -tl - 2.0 * ml - bl + tr + 2.0 * mr + br;
  float gy = -tl - 2.0 * tc - tr + bl + 2.0 * bc + br;

  float mag = clamp(sqrt(gx * gx + gy * gy) * u_intensity, 0.0, 1.0);
  if (u_invert) mag = 1.0 - mag;

  vec4 src = texture(u_image, v_uv);
  vec3 edgeColor = vec3(mag);
  vec3 finalRgb = mix(edgeColor, edgeColor * src.rgb, u_mixSource);

  fragColor = vec4(finalRgb, src.a);
}
`;

export const edgeDetectNode: NodeDefinition = {
  type: 'filter.edge_detect',
  category: 'Filter',
  label: 'Edge Detect',
  description: 'Extracts sharp image contours and boundaries via Sobel convolution',
  keywords: ['edge', 'detect', 'sobel', 'contour', 'line', 'outline', 'sketch'],
  targetBackend: 'webgl2',
  inputs: [
    { id: 'image', name: 'Source', type: 'image' },
  ],
  outputs: [
    { id: 'output', name: 'Edges', type: 'image' },
  ],
  params: [
    {
      id: 'intensity',
      name: 'Edge Strength',
      type: 'slider',
      defaultValue: 1.5,
      min: 0.1,
      max: 5.0,
      step: 0.1,
      precision: 1,
    },
    {
      id: 'invert',
      name: 'Invert (Dark Edges)',
      type: 'boolean',
      defaultValue: false,
      uiHint: 'toggle',
    },
    {
      id: 'mixSource',
      name: 'Retain Image Color',
      type: 'slider',
      defaultValue: 0,
      min: 0,
      max: 100,
      step: 1,
      unit: '%',
    },
  ],
  processor: createShaderProcessor({
    fragmentShader: EDGE_DETECT_SHADER,
    getUniforms: (inputs, params, dimensions) => ({
      u_image: inputs.image as unknown as import('../../rendering/types').TextureHandle,
      u_resolution: [dimensions.width, dimensions.height],
      u_intensity: Number(params.intensity) || 1.5,
      u_invert: Boolean(params.invert),
      u_mixSource: (Number(params.mixSource) || 0) / 100,
    }),
  }),
};
